/// <amd-module name="TFS/Admin/Common/RestClient/User" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   vssf\client\webapi\httpclients\clientgeneratorconfigs\user.genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as User from "../Contracts/User";
import * as VS_Services from "VSS/Platform/Contracts/WebApi";

export interface IUserRestClient extends IVssRestClient {
    /**
     * Deletes an attribute for the given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param attributeName - The name of the attribute to delete.
     */
    deleteAttribute(descriptor: string, attributeName: string): Promise<void>;
    /**
     * Retrieves an attribute for a given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param attributeName - The name of the attribute to retrieve.
     */
    getAttribute(descriptor: string, attributeName: string): Promise<User.UserAttribute>;
    /**
     * Retrieves attributes for a given user. May return subset of attributes providing continuation token to retrieve the next batch
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param continuationToken - The token telling server to return the next chunk of attributes from where it stopped last time. This must either be null or be a value returned from the previous call to this API
     * @param queryPattern - The wildcardable pattern for the attribute names to be retrieved, e.g. queryPattern=visualstudio.14.*
     * @param modifiedAfter - The optional date/time of the minimum modification date for attributes to be retrieved, e.g. modifiedafter=2017-04-12T15:00:00.000Z
     */
    queryAttributes(descriptor: string, continuationToken?: string, queryPattern?: string, modifiedAfter?: Date): Promise<User.UserAttributes>;
    /**
     * Updates multiple attributes for a given user.
     * 
     * @param attributeParametersList - The list of attribute data to update.  Existing values will be overwritten.
     * @param descriptor - The identity of the user for the operation.
     */
    setAttributes(attributeParametersList: User.SetUserAttributeParameters[], descriptor: string): Promise<User.UserAttribute[]>;
    /**
     * Deletes a user's avatar.
     * 
     * @param descriptor - The identity of the user for the operation.
     */
    deleteAvatar(descriptor: string): Promise<void>;
    /**
     * Retrieves the user's avatar.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param size - The size to retrieve, e.g. small, medium (default), or large.
     * @param format - The format for the response. Can be null. Accepted values: "png", "json"
     */
    getAvatar(descriptor: string, size?: User.AvatarSize, format?: string): Promise<User.Avatar>;
    /**
     * Creates or updates an avatar to be associated with a given user.
     * 
     * @param avatar - The avatar to set. The Image property must contain the binary representation of the image, in either jpg or png format.
     * @param descriptor - The identity of the user for the operation.
     */
    setAvatar(avatar: User.Avatar, descriptor: string): Promise<void>;
    /**
     * @param avatar - 
     * @param descriptor - 
     * @param size - 
     * @param displayName - 
     */
    createAvatarPreview(avatar: User.Avatar, descriptor: string, size?: User.AvatarSize, displayName?: string): Promise<User.Avatar>;
    /**
     * @param descriptor - 
     */
    getDescriptor(descriptor: string): Promise<VS_Services.SubjectDescriptor>;
    /**
     * @param descriptor - 
     */
    disableUserProfileSync(descriptor: VS_Services.SubjectDescriptor): Promise<void>;
    /**
     * @param descriptor - 
     */
    enableUserProfileSync(descriptor: VS_Services.SubjectDescriptor): Promise<void>;
    /**
     * Confirms preferred email for a given user.
     * 
     * @param confirmationParameters - 
     * @param descriptor - The descriptor identifying the user for the operation.
     */
    confirmMail(confirmationParameters: User.MailConfirmationParameters, descriptor: string): Promise<void>;
    /**
     * Returns a user's most recently accessed hosts.
     * 
     * @param descriptor - A user descriptor
     */
    getMostRecentlyAccessedHosts(descriptor: string): Promise<User.AccessedHost[]>;
    /**
     * Updates users' most recently accessed hosts.
     * 
     * @param parametersList - A list of update parameters
     */
    updateMostRecentlyAccessedHosts(parametersList: User.AccessedHostsParameters[]): Promise<void>;
    /**
     * @param descriptor - 
     */
    getStorageKey(descriptor: string): Promise<string>;
    /**
     * Retrieves the default data for the authenticated user.
     * 
     */
    getUserDefaults(): Promise<User.User>;
    /**
     * Creates a new user.
     * 
     * @param userParameters - The parameters to be used for user creation.
     * @param createLocal - 
     */
    createUser(userParameters: User.CreateUserParameters, createLocal?: boolean): Promise<User.User>;
    /**
     * @param descriptor - 
     */
    deleteUser(descriptor: VS_Services.SubjectDescriptor): Promise<void>;
    /**
     * Retrieves the data for a given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param createIfNotExists - Whether to auto-provision the authenticated user
     */
    getUser(descriptor: string, createIfNotExists?: Boolean): Promise<User.User>;
    /**
     * Updates an existing user.
     * 
     * @param userParameters - The parameters to be used for user update.
     * @param descriptor - The identity of the user for the operation.
     */
    updateUser(userParameters: User.UpdateUserParameters, descriptor: string): Promise<User.User>;
}

class UserRestClient extends RestClientBase implements IUserRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Deletes an attribute for the given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param attributeName - The name of the attribute to delete.
     */
    public async deleteAttribute(
        descriptor: string,
        attributeName: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.2",
            method: "DELETE",
            routeTemplate: "{descriptor}/Attributes/{*attributeName}",
            routeValues: {
                descriptor: descriptor,
                attributeName: attributeName
            }
        });
    }

    /**
     * Retrieves an attribute for a given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param attributeName - The name of the attribute to retrieve.
     */
    public async getAttribute(
        descriptor: string,
        attributeName: string
        ): Promise<User.UserAttribute> {

        return this.beginRequest<User.UserAttribute>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{descriptor}/Attributes/{*attributeName}",
            routeValues: {
                descriptor: descriptor,
                attributeName: attributeName
            }
        });
    }

    /**
     * Retrieves attributes for a given user. May return subset of attributes providing continuation token to retrieve the next batch
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param continuationToken - The token telling server to return the next chunk of attributes from where it stopped last time. This must either be null or be a value returned from the previous call to this API
     * @param queryPattern - The wildcardable pattern for the attribute names to be retrieved, e.g. queryPattern=visualstudio.14.*
     * @param modifiedAfter - The optional date/time of the minimum modification date for attributes to be retrieved, e.g. modifiedafter=2017-04-12T15:00:00.000Z
     */
    public async queryAttributes(
        descriptor: string,
        continuationToken?: string,
        queryPattern?: string,
        modifiedAfter?: Date
        ): Promise<User.UserAttributes> {

        const queryValues: any = {
            continuationToken: continuationToken,
            queryPattern: queryPattern,
            modifiedAfter: modifiedAfter
        };

        return this.beginRequest<User.UserAttributes>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{descriptor}/Attributes/{*attributeName}",
            routeValues: {
                descriptor: descriptor
            },
            queryParams: queryValues
        });
    }

    /**
     * Updates multiple attributes for a given user.
     * 
     * @param attributeParametersList - The list of attribute data to update.  Existing values will be overwritten.
     * @param descriptor - The identity of the user for the operation.
     */
    public async setAttributes(
        attributeParametersList: User.SetUserAttributeParameters[],
        descriptor: string
        ): Promise<User.UserAttribute[]> {

        return this.beginRequest<User.UserAttribute[]>({
            apiVersion: "5.0-preview.2",
            method: "PATCH",
            routeTemplate: "{descriptor}/Attributes/{*attributeName}",
            routeValues: {
                descriptor: descriptor
            },
            body: attributeParametersList
        });
    }

    /**
     * Deletes a user's avatar.
     * 
     * @param descriptor - The identity of the user for the operation.
     */
    public async deleteAvatar(
        descriptor: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{descriptor}/Avatar",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * Retrieves the user's avatar.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param size - The size to retrieve, e.g. small, medium (default), or large.
     * @param format - The format for the response. Can be null. Accepted values: "png", "json"
     */
    public async getAvatar(
        descriptor: string,
        size?: User.AvatarSize,
        format?: string
        ): Promise<User.Avatar> {

        const queryValues: any = {
            size: size,
            format: format
        };

        return this.beginRequest<User.Avatar>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{descriptor}/Avatar",
            routeValues: {
                descriptor: descriptor
            },
            queryParams: queryValues
        });
    }

    /**
     * Creates or updates an avatar to be associated with a given user.
     * 
     * @param avatar - The avatar to set. The Image property must contain the binary representation of the image, in either jpg or png format.
     * @param descriptor - The identity of the user for the operation.
     */
    public async setAvatar(
        avatar: User.Avatar,
        descriptor: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "PUT",
            routeTemplate: "{descriptor}/Avatar",
            routeValues: {
                descriptor: descriptor
            },
            body: avatar
        });
    }

    /**
     * @param avatar - 
     * @param descriptor - 
     * @param size - 
     * @param displayName - 
     */
    public async createAvatarPreview(
        avatar: User.Avatar,
        descriptor: string,
        size?: User.AvatarSize,
        displayName?: string
        ): Promise<User.Avatar> {

        const queryValues: any = {
            size: size,
            displayName: displayName
        };

        return this.beginRequest<User.Avatar>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{descriptor}/AvatarPreview",
            routeValues: {
                descriptor: descriptor
            },
            queryParams: queryValues,
            body: avatar
        });
    }

    /**
     * @param descriptor - 
     */
    public async getDescriptor(
        descriptor: string
        ): Promise<VS_Services.SubjectDescriptor> {

        return this.beginRequest<VS_Services.SubjectDescriptor>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{descriptor}/Descriptor",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * @param descriptor - 
     */
    public async disableUserProfileSync(
        descriptor: VS_Services.SubjectDescriptor
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{descriptor}/DisableUserProfileSync",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * @param descriptor - 
     */
    public async enableUserProfileSync(
        descriptor: VS_Services.SubjectDescriptor
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{descriptor}/EnableUserProfileSync",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * Confirms preferred email for a given user.
     * 
     * @param confirmationParameters - 
     * @param descriptor - The descriptor identifying the user for the operation.
     */
    public async confirmMail(
        confirmationParameters: User.MailConfirmationParameters,
        descriptor: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{descriptor}/MailConfirmation",
            routeValues: {
                descriptor: descriptor
            },
            body: confirmationParameters
        });
    }

    /**
     * Returns a user's most recently accessed hosts.
     * 
     * @param descriptor - A user descriptor
     */
    public async getMostRecentlyAccessedHosts(
        descriptor: string
        ): Promise<User.AccessedHost[]> {

        return this.beginRequest<User.AccessedHost[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{descriptor}/MostRecentlyAccessedHosts",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * Updates users' most recently accessed hosts.
     * 
     * @param parametersList - A list of update parameters
     */
    public async updateMostRecentlyAccessedHosts(
        parametersList: User.AccessedHostsParameters[]
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "MostRecentlyAccessedHosts",
            body: parametersList
        });
    }

    /**
     * @param descriptor - 
     */
    public async getStorageKey(
        descriptor: string
        ): Promise<string> {

        return this.beginRequest<string>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{descriptor}/StorageKey",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * Retrieves the default data for the authenticated user.
     * 
     */
    public async getUserDefaults(
        ): Promise<User.User> {

        return this.beginRequest<User.User>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "UserDefaults"
        });
    }

    /**
     * Creates a new user.
     * 
     * @param userParameters - The parameters to be used for user creation.
     * @param createLocal - 
     */
    public async createUser(
        userParameters: User.CreateUserParameters,
        createLocal?: boolean
        ): Promise<User.User> {

        const queryValues: any = {
            createLocal: createLocal
        };

        return this.beginRequest<User.User>({
            apiVersion: "5.0-preview.2",
            method: "POST",
            routeTemplate: "{descriptor}",
            queryParams: queryValues,
            body: userParameters
        });
    }

    /**
     * @param descriptor - 
     */
    public async deleteUser(
        descriptor: VS_Services.SubjectDescriptor
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.2",
            method: "DELETE",
            routeTemplate: "{descriptor}",
            routeValues: {
                descriptor: descriptor
            }
        });
    }

    /**
     * Retrieves the data for a given user.
     * 
     * @param descriptor - The identity of the user for the operation.
     * @param createIfNotExists - Whether to auto-provision the authenticated user
     */
    public async getUser(
        descriptor: string,
        createIfNotExists?: Boolean
        ): Promise<User.User> {

        return this.beginRequest<User.User>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{descriptor}",
            routeValues: {
                descriptor: descriptor
            },
            customHeaders: {
                "X-VSS-FaultInUser": createIfNotExists,
            }
        });
    }

    /**
     * Updates an existing user.
     * 
     * @param userParameters - The parameters to be used for user update.
     * @param descriptor - The identity of the user for the operation.
     */
    public async updateUser(
        userParameters: User.UpdateUserParameters,
        descriptor: string
        ): Promise<User.User> {

        return this.beginRequest<User.User>({
            apiVersion: "5.0-preview.2",
            method: "PATCH",
            routeTemplate: "{descriptor}",
            routeValues: {
                descriptor: descriptor
            },
            body: userParameters
        });
    }

}

export const UserClientName = "User-AdminArea.IUserRestClient";

export function getUserClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IUserRestClient {
    return pageContext.getRestClient<IUserRestClient>(UserClientName, options);
}

RestClients.add(UserClientName, { 
    factory: UserRestClient,
    options: {
        serviceInstanceType: "00000038-0000-8888-8000-000000000000"
    }
});
